import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PermissionModal from '../../popmodel/PermissionModal1';
const API_BASE_URL = process.env.REACT_APP_API_URL;
function RoleManager() {
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [modalPermissions, setModalPermissions] = useState([]);
  const navigate = useNavigate();

  // Load user permissions and role from localStorage
  const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const userRole = JSON.parse(localStorage.getItem('role') || '{}')?.name;

  const canRead = userPermissions.includes("read_RoleAccessControl");
  const canCreate = userPermissions.includes("create_RoleAccessControl");
  const canEdit = userPermissions.includes("update_RoleAccessControl");
  const canDelete = userPermissions.includes("delete_RoleAccessControl");
  const canToggleStatus = userPermissions.includes("update_RoleAccessControl");

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/roles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      let fetchedRoles = res.data || [];

      if (userRole === 'Seller') {
        fetchedRoles = fetchedRoles.filter(role => role.name === 'Buyer');
      }

      setRoles(fetchedRoles);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  }, [userRole]);

  useEffect(() => {
    if (canRead) fetchRoles();
  }, [canRead, fetchRoles]);

  const handleDeleteRole = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role permanently?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/roles/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      alert('Failed to delete role');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/roles/toggle-active/${id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchRoles();
    } catch (err) {
      console.error('Failed to toggle role status:', err);
      alert('Failed to change status');
    }
  };

  const handlePermissionClick = (permissions) => {
    setModalPermissions(permissions);
    setShowPermissionModal(true);
  };

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#102234' }} className="fw-bold">Role & Access Management</h2>
        <button
          className="btn d-flex align-items-center text-white"
          style={{ backgroundColor: '#102234' }}
          onClick={() => navigate('/roles/add')}
          disabled={!canCreate}
          title={!canCreate ? "You don't have permission to create roles" : ""}
        >
          <FaPlus className="me-2" /> Create New Role & Access
        </button>
      </div>

      <div className="mb-3">
        <input
          type="text"
          placeholder="🔍 Search by role name"
          className="form-control w-50"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover bg-white shadow-sm">
          <thead className="table-light">
            <tr>
              <th>No.</th>
              <th>Role</th>
              <th>Permissions</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoles.length > 0 ? (
              filteredRoles.map((role, index) => (
                <tr key={role._id}>
                  <td>{index + 1}</td>
                  <td>{role.name}</td>
                  <td>
                    <span
                      className="text-primary text-decoration-underline"
                      role="button"
                      onClick={() => handlePermissionClick(role.permissions || [])}
                    >
                      {role.permissions.length} Permission{role.permissions.length !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td>
                    <div className="form-check form-switch">
                      <input
                        className={`form-check-input ${role.isActive ? 'bg-success border-success' : 'bg-danger border-danger'}`}
                        type="checkbox"
                        checked={role.isActive}
                        onChange={() => handleToggleActive(role._id)}
                        role="switch"
                        disabled={!canToggleStatus}
                        title={!canToggleStatus ? "You don't have permission to change status" : ""}
                      />
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm me-2 text-dark btn-outline-primary"
                      onClick={() => navigate(`/roles/edit/${role._id}`)}
                      title={!canEdit ? "You don't have permission to edit roles" : "Edit Role"}
                      disabled={!canEdit}
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role._id)}
                      className="btn btn-sm btn-outline-danger"
                      title={!canDelete ? "You don't have permission to delete roles" : "Delete Role"}
                      disabled={!canDelete}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">No roles found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPermissionModal && (
        <PermissionModal
          permissions={modalPermissions}
          onClose={() => setShowPermissionModal(false)}
        />
      )}
    </div>
  );
}

export default RoleManager;
